import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { UserPrivilegeEnum } from '../../../../enums/rights/user-privilege.enum';
import { RightsScopeEnum } from '../../../../enums/rights/rights-scope.enum';
import { RUserFunction } from './r-user-function.action';
import { UserRightsFunctionModel } from '../../../../models/rights/user-rights-function/user-rights-function.model';
import featureFlags from '../../../../feature-flags';
import { ResetStateDefaults } from '../../../base-action-class.model';

interface RUserFunctionXsStateModel {
  //global
  viewGlobalUserFunction: string;
  createGlobalUserFunction: string;
  editGlobalUserFunction: string;
  deleteGlobalUserFunction: string;
  //organization
  viewOrganizationUserFunction: string;
  createOrganizationUserFunction: string;
  editOrganizationUserFunction: string;
  deleteOrganizationUserFunction: string;
}

const getDefaults = (): RUserFunctionXsStateModel => ({
  viewGlobalUserFunction: null,
  createGlobalUserFunction: null,
  editGlobalUserFunction: null,
  deleteGlobalUserFunction: null,

  viewOrganizationUserFunction: null,
  createOrganizationUserFunction: null,
  editOrganizationUserFunction: null,
  deleteOrganizationUserFunction: null
});

@State<RUserFunctionXsStateModel>({
  name: 'rUserFunction',
  defaults: getDefaults()
})
@Injectable()
export class RUserFunctionXsState {

  //region SELECTOR
  @Selector()
  static canCreateAnyUserFunction(state: RUserFunctionXsStateModel): boolean {
    return state.createGlobalUserFunction != null || state.createOrganizationUserFunction != null;
  }

  @Selector()
  static canViewAnyUserFunction(state: RUserFunctionXsStateModel): boolean {
    return state.viewGlobalUserFunction != null || state.viewOrganizationUserFunction != null;
  }

  static canViewUserFunction(scope: RightsScopeEnum) {
    return createSelector([RUserFunctionXsState], (state: RUserFunctionXsStateModel): boolean => {
      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.viewGlobalUserFunction != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.viewOrganizationUserFunction != null;
      }
      return false;
    });
  }

  static canCreateUserFunction(scope: RightsScopeEnum) {
    return createSelector([RUserFunctionXsState], (state: RUserFunctionXsStateModel): boolean => {
      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.createGlobalUserFunction != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.createOrganizationUserFunction != null;
      }
      return false;
    });
  }

  static canEditUserFunction(scope: RightsScopeEnum) {
    return createSelector([RUserFunctionXsState], (state: RUserFunctionXsStateModel): boolean => {
      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.editGlobalUserFunction != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.editOrganizationUserFunction != null;
      }
      return false;
    });
  }

  static canDeleteUserFunction(scope: RightsScopeEnum) {
    return createSelector([RUserFunctionXsState], (state: RUserFunctionXsStateModel): boolean => {
      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.deleteGlobalUserFunction != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.deleteOrganizationUserFunction != null;
      }
      return false;
    });
  }

  static canDeleteUserFunctionByModel(model: UserRightsFunctionModel) {
    return createSelector([RUserFunctionXsState], (state: RUserFunctionXsStateModel): boolean => {
      const scope = this.getScopeByModel(model);

      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.deleteGlobalUserFunction != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.deleteOrganizationUserFunction != null;
      }
      return false;
    });
  }

  static canEditUserFunctionByModel(model: UserRightsFunctionModel) {
    return createSelector([RUserFunctionXsState], (state: RUserFunctionXsStateModel): boolean => {
      const scope = this.getScopeByModel(model);

      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.editGlobalUserFunction != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.editOrganizationUserFunction != null;
      }
      return false;
    });
  }

  //endregion

  private static getScopeByModel(model: UserRightsFunctionModel) {
    if (model.isSystem) {
      return RightsScopeEnum.GLOBAL;
    } else if (model.isSystem === false) {
      return RightsScopeEnum.ORGANIZATION;
    } else {
      return RightsScopeEnum.ERROR;
    }
  }

  //region ACTIONS
  @Action(RUserFunction.SetUserFunctionPrivileges)
  public SetUserFunctionPrivileges(ctx: StateContext<RUserFunctionXsStateModel>, action: RUserFunction.SetUserFunctionPrivileges) {
    const state = ctx.getState();
    const userRightsSystemPrivileges = [...action.activeUserRightsRoleDtoModel.userRightsSystemPrivileges];

    //find user privilege data model
    const viewGlobalUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.VIEW_SYSTEM_USER_RIGHTS_FUNCTION);
    const createGlobalUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.CREATE_SYSTEM_USER_RIGHTS_FUNCTION);
    const editGlobalUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.EDIT_SYSTEM_USER_RIGHTS_FUNCTION);
    const deleteGlobalUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.DELETE_SYSTEM_USER_RIGHTS_FUNCTION);

    const viewOrganizationUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.VIEW_ORGANIZATION_USER_RIGHTS_FUNCTION);
    const createOrganizationUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.CREATE_ORGANIZATION_USER_RIGHTS_FUNCTION);
    const editOrganizationUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.EDIT_ORGANIZATION_USER_RIGHTS_FUNCTION);
    const deleteOrganizationUserFunction = userRightsSystemPrivileges.find(id => id === UserPrivilegeEnum.DELETE_ORGANIZATION_USER_RIGHTS_FUNCTION);


    //set the state
    ctx.setState({
      ...state,
      viewGlobalUserFunction: viewGlobalUserFunction,
      createGlobalUserFunction: createGlobalUserFunction,
      editGlobalUserFunction: editGlobalUserFunction,
      deleteGlobalUserFunction: deleteGlobalUserFunction,

      viewOrganizationUserFunction: viewOrganizationUserFunction,
      createOrganizationUserFunction: featureFlags.ORGANIZATION_USER_RIGHTS_FUNCTIONS_ENABLED ? createOrganizationUserFunction : null,
      editOrganizationUserFunction: featureFlags.ORGANIZATION_USER_RIGHTS_FUNCTIONS_ENABLED ? editOrganizationUserFunction : null,
      deleteOrganizationUserFunction: featureFlags.ORGANIZATION_USER_RIGHTS_FUNCTIONS_ENABLED ? deleteOrganizationUserFunction : null,
    });
  }

  @Action(ResetStateDefaults)
  public resetDefaults(ctx: StateContext<RUserFunctionXsStateModel>): void {
    ctx.setState({ ...getDefaults() });
  }

  //endregion
}
